import React, { useCallback, useMemo, useState } from 'react';
import { isArray, get, pickBy } from 'lodash';
import { useSelector } from 'react-redux';
import { Box } from '@mui/material';
import {
  DataGridPro,
  GridColumnVisibilityModel,
  GridPinnedColumnFields,
  GridToolbarColumnsButton,
} from '@mui/x-data-grid-pro';
import { ISearchResultsGrid } from './_types';
import { useAppInfo } from 'utils/hooks/useAppInfo';
import { useGrid } from 'utils/hooks/useGrid';
import { useGridColumns } from 'components/SearchResultsMUI/useGridColumns';
import useGridLocalization from 'components/SearchResultsMUI/useGridLocalization';
import { useGridSettings } from 'components/SearchResultsMUI/useGridSettings';
import {
  DATA_GRID_DEFAULT_SORTING,
  DATA_GRID_ROW_HEIGHT,
  DATAGRID_SETTINGS_COLUMN_ORDER_MODEL,
  DATAGRID_SETTINGS_COLUMN_PINNED_COLUMNS,
  DATAGRID_SETTINGS_COLUMN_SORT_MODEL,
  DATAGRID_SETTINGS_COLUMN_WIDTHS,
} from 'constants/constants';
import type { RootState } from 'store/store';

const greenRowSelectedDarkMode = '#439027';
const greenRowSelectedLightMode = '#b2ff96';

export const SearchResultsGrid: React.FC<ISearchResultsGrid> = ({ gridKey }) => {
  const { compactMode } = useAppInfo();
  const gridLocalization = useGridLocalization();
  const { setGridSettingsAndStore } = useGrid();

  const defaultFilter = { items: [] };

  const [searchFilterModel, setSearchFilterModel] = useState<any>(defaultFilter);

  // If detail panel is enabled, indexes are one higher than they should be
  // If selecting is enabled, indexes are one higher than they should be
  const indexShiftForReordering = 0;

  const rows: any[] = [
    {
      accessionNumber: 'Číslo žádanky',
      dateTime: '2025-09-17T10:23:00',
      studyDescription: 'Screening',
      referringPhysician: {
        name: {
          firstName: 'Lékař',
          lastName: 'Vyšetřující',
        },
      },
      availability: 3,
      statuses: [
        {
          icon: 'lock',
          name: '{Closed}',
        },
      ],
      studyExtendedInfo: {
        studyTags: ['#tag', '#Active tag', '#tag*^*#Active tag'],
        stationAet: 'REALAPI',
        comments: { count: 1 },
        sendInfos: [{ sejf: true }],
        folderInfos: [{ name: 'test' }],
        hasAttachment: true,
        hasShreddingProposal: true,
      },
      name: 'Prefix LAST FIRST, Middle, Suffix',
      patientId: '9311073772',
      patientSex: 'M',
      patientSexTranslated: 'Muž',
      patientBirthDate: '1993-11-07',
      modality: 'OT',
      noOfInstances: '2/14',
      atLeastOneSeriesNotShown: false,
      archiveName: 'DEV_DPGW',
      archiveId: 12,

      id: 1,
      ok: true,
    },
  ];

  const { columns } = useGridColumns({
    action: '',
    actions: [],
    isShredding: false,
    isShreddingPrint: false,
    isArchive: false,
    operations: {},
    isRequest: false,
    nameAsLink: true,
    handleCloseDialog: undefined,
    importProps: undefined,
    disableStudyAndPatientForm: false,
    isActionFirstColumn: false,
    showReferringPhysician: true,
    uniqModalities: [],
    uniqPatientSexes: [],
    gridKey,
    showHeaderSpinner: true,
  });

  const {
    gridSettings,
    getUpdatedColumnWidthsAfterResize,
    getGridColumnsSort,
    getUpdatedColumnOrderAfterReordering,
    getPinnedColumnsFromGridSettings,
  } = useGridSettings();

  const pinnedColumns = getPinnedColumnsFromGridSettings(gridKey);

  const defaultHiddenColumns = useMemo(
    () => ({
      comments: false,
      folderInfos: false,
      hasAttachment: false,
      referringPhysician: false,
      sendInfos: false,
      shredding: false,
      stationAet: false,
      studyStatus: false,
      tags: false,
    }),
    [],
  );

  const setAndStore = useCallback(
    (gridSettingsForAPI: any, storeToDb: boolean = true) => {
      const gridSettingsForRedux = {
        ...gridSettings,
        [gridKey]: gridSettingsForAPI,
      };

      setGridSettingsAndStore(
        {
          key: gridKey,
          gridSettingsForAPI,
          gridSettingsForRedux,
        },
        storeToDb,
      );
    },
    [gridKey, gridSettings, setGridSettingsAndStore],
  );

  const columnVisibilityModel = useSelector((state: RootState) =>
    get(state, `app.gridSettings.${gridKey}.columnVisibilityModel`, defaultHiddenColumns),
  );

  const setColumnVisibilityModel = useCallback(
    (newModel: GridColumnVisibilityModel) => {
      const gridSettingsForAPI = {
        ...get(gridSettings, gridKey, {}),
        // ukládáme pouze sloupce, které jsou false (= schované)
        columnVisibilityModel: pickBy(newModel, (item: Boolean) => item === false),
      };

      setAndStore(gridSettingsForAPI);
    },
    [gridKey, setAndStore, gridSettings],
  );

  const onColumnWidthChange = useCallback(
    async (changedColumn: any) => {
      const columnNewWidth = changedColumn.width;
      const columnName = changedColumn.colDef.field;

      const gridSettingsForAPI = {
        ...get(gridSettings, gridKey, {}),
        [DATAGRID_SETTINGS_COLUMN_WIDTHS]: getUpdatedColumnWidthsAfterResize(
          columns,
          columnName,
          columnNewWidth,
        ),
      };
      setAndStore(gridSettingsForAPI);
    },
    [gridKey, gridSettings, setAndStore, columns, getUpdatedColumnWidthsAfterResize],
  );

  const onColumnOrderChange = useCallback(
    async (params: any) => {
      const newColumnOrder = getUpdatedColumnOrderAfterReordering(
        columns,
        params.column.field,
        params.oldIndex - indexShiftForReordering,
        params.targetIndex - indexShiftForReordering,
      );

      const gridSettingsForAPI = {
        ...get(gridSettings, gridKey, {}),
        [DATAGRID_SETTINGS_COLUMN_ORDER_MODEL]: newColumnOrder.map((column) => column.field),
      };
      setAndStore(gridSettingsForAPI);
    },
    [
      columns,
      getUpdatedColumnOrderAfterReordering,
      gridKey,
      gridSettings,
      indexShiftForReordering,
      setAndStore,
    ],
  );

  const onSortModelChange = useCallback(
    async (newSortModel: any) => {
      const gridSettingsForAPI = {
        ...get(gridSettings, gridKey, {}),
        [DATAGRID_SETTINGS_COLUMN_SORT_MODEL]: newSortModel,
      };
      setAndStore(gridSettingsForAPI);
    },
    [gridKey, gridSettings, setAndStore],
  );

  const onFilterModelChange = useCallback(async (newFilterModel: any) => {
    setSearchFilterModel(newFilterModel);
  }, []);

  const onPinnedColumnsChange = useCallback(
    async (updatedPinnedColumns: GridPinnedColumnFields) => {
      const gridSettingsForAPI = {
        ...get(gridSettings, gridKey, {}),
        [DATAGRID_SETTINGS_COLUMN_PINNED_COLUMNS]: updatedPinnedColumns,
      };
      setAndStore(gridSettingsForAPI);
    },
    [gridKey, gridSettings, setAndStore],
  );

  const gridSortModel = getGridColumnsSort(gridKey);

  const QuickSearchToolbar = (props: any) => {
    return (
      <Box style={{ padding: '5px', display: 'flex', justifyContent: 'space-between' }}>
        <Box>
          <GridToolbarColumnsButton />
        </Box>
      </Box>
    );
  };

  return (
    <DataGridPro
      headerFilters={false}
      sortingOrder={DATA_GRID_DEFAULT_SORTING}
      sx={{
        '--DataGrid-overlayHeight': '150px',
        height: '100% !important',
        width: 'auto',
        '& .css.greenRowSelectedDarkMode': {
          backgroundColor: greenRowSelectedDarkMode,
          hover: { backgroundColor: greenRowSelectedDarkMode },
        },
        '& .css.greenRowSelectedLightMode': {
          backgroundColor: greenRowSelectedLightMode,
          hover: { backgroundColor: greenRowSelectedLightMode },
        },
      }}
      rows={rows}
      columns={columns}
      slots={{ toolbar: QuickSearchToolbar }}
      density={compactMode ? 'compact' : 'standard'}
      rowHeight={DATA_GRID_ROW_HEIGHT}
      slotProps={{
        columnsPanel: { className: 'react-tour-column-menu' },
        filterPanel: { className: 'react-tour-filter-panel' } as any,
        footer: { ...(compactMode ? { sx: { minHeight: 30 } } : {}) },
        headerFilterCell: { InputComponentProps: { size: 'small' } },
      }}
      localeText={gridLocalization}
      columnVisibilityModel={columnVisibilityModel}
      onColumnVisibilityModelChange={setColumnVisibilityModel}
      onColumnWidthChange={onColumnWidthChange}
      onColumnOrderChange={onColumnOrderChange}
      sortModel={isArray(gridSortModel) ? gridSortModel : []}
      onSortModelChange={onSortModelChange}
      pinnedColumns={pinnedColumns}
      onPinnedColumnsChange={onPinnedColumnsChange}
      filterModel={searchFilterModel}
      onFilterModelChange={onFilterModelChange}
      hideFooter={true}
      disableColumnFilter={true}
      disableColumnPinning={false}
    />
  );
};
